import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Container, Grid, GridItem, IconName, Stack } from '@optimus-web/core';
import { LocalizedParagraph, LocalizedSubsectionTitle, TooltippedIcon, TooltippedSpan } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, OrderItem } from '@mews-commander/types';
import {
    createZodFormResolver,
    FormCounterField,
    FormTextareaField,
    LocalizedCurrencyValue,
    lt,
    NumberService,
    useFormControls,
} from '@mews/framework';
import {
    GeneralPricingMode,
    OrderItem as GeneratedOrderItem,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { ProductChargingMode, UnicodeEntity } from '@mews/enums';
import { useCultureCode } from '@mews-commander/core';
import { getCurrencyLocalizationInfo, getNumberCultureInfo } from '@mews-commander/core/legacy-globalization';
import { createUpdateValue, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { CurrencyInputField } from '@mews-commander/form';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { fetchCurrentEnterpriseBillsByAccountIds } from '@mews-commander/legacy-entities/src/actions';
import { BILLING_EXTENT, DEFAULT_BILL_FETCH_EXTENT } from '@mews-commander/legacy-entities/src/actions/bills';
import { CurrencyFormatHint } from '../../../components/CurrencyFormatHint';
import { ExtendedOrderItemName } from '../components/ExtendedOrderItemName';
import { EditItemDialogTotalAmount } from './EditItemDialogTotalAmount';
import { useCalculateEditAmount } from './hooks/useCalculateEditAmount';
import { EditItemDialogFields, EditItemFormValues, editItemSchema } from './constants';

interface Props {
    orderItem: GeneratedOrderItem;
    closeDialog: () => void;
    onSubmitSuccess: () => void;
}

export const EditItemDialogContent = forwardRef(({ closeDialog, orderItem, onSubmitSuccess }: Props, ref) => {
    const dispatch = useCommanderThunkDispatch();
    const { pricing } = useCurrentEnterprise();
    const hasNetPricing = pricing === GeneralPricingMode.Net;

    const baseValue = (hasNetPricing ? orderItem.unitAmount.netValue : orderItem.unitAmount.grossValue) as number;

    const { mutateAsync } = useRepositoryMutation('orderItems', 'doublePostingUpdate', {
        onSuccess: async () => {
            await dispatch(
                fetchCurrentEnterpriseBillsByAccountIds({
                    accountIds: [orderItem.ownerId],
                    extent: { ...DEFAULT_BILL_FETCH_EXTENT, ...BILLING_EXTENT },
                })
            );
        },
    });

    const defaultValues = {
        [EditItemDialogFields.Reason]: '',
        [EditItemDialogFields.Quantity]: orderItem.unitMultiplier,
        [EditItemDialogFields.UnitPrice]: baseValue,
    };

    const { renderFormContent, watch, submit } = useFormControls<EditItemFormValues>({
        resolver: createZodFormResolver(editItemSchema),
        onSubmit: async ({ reason, quantity, unitPrice }) => {
            const originalUnitPrice = baseValue * orderItem.personMultiplier * orderItem.nightMultiplier;

            await mutateAsync({
                orderItemUpdates: [
                    {
                        orderItemId: orderItem.id,
                        // @ts-expect-error Will be solved when backend types are updated.
                        unitPrice: createUpdateValue(originalUnitPrice, unitPrice),
                        // @ts-expect-error Will be solved when backend types are updated.
                        unitMultiplier: createUpdateValue(orderItem.unitMultiplier, quantity),
                    },
                ],
                reason,
            });
            onSubmitSuccess();
            closeDialog();
        },
        defaultValues,
    });

    useImperativeHandle(
        ref,
        () => ({
            submit,
        }),
        [submit]
    );

    const cultureCode = useCultureCode();
    const numberCultureInfo = useCommanderSelector((state) => getNumberCultureInfo(state, { cultureCode }));

    const unitPrice = watch(EditItemDialogFields.UnitPrice);
    const isValidUnitPrice = unitPrice && !Number.isNaN(+unitPrice);

    const parsedUnitPrice =
        typeof unitPrice === 'string' ? NumberService.unformat(unitPrice, numberCultureInfo) : unitPrice;

    const currencyLocalizationInfo = useSelector((state: ApplicationState) =>
        getCurrencyLocalizationInfo(state, {
            currencyCode: orderItem.unitAmount.currency,
            cultureCode,
        })
    );

    const { value: unitPriceWithTax } = useCalculateEditAmount({
        price: isValidUnitPrice ? parsedUnitPrice : 0,
        taxValues: orderItem.totalAmount.taxValues || [],
    });

    const quantityLabel = useMemo(() => {
        if (orderItem.productChargingMode === ProductChargingMode.PerPerson) {
            return lt('QuantityPerPerson');
        } else if (orderItem.productChargingMode === ProductChargingMode.PerPersonPerTimeUnit) {
            return lt('QuantityPerPersonPerDay');
        }

        return lt('Quantity');
    }, [orderItem?.productChargingMode]);

    const quantityBottomCaption = useMemo(() => {
        if (orderItem.productChargingMode === ProductChargingMode.PerPersonPerTimeUnit) {
            return lt('QuantityPerPersonPerDayBottomCaption');
        }

        return undefined;
    }, [orderItem.productChargingMode]);

    return renderFormContent(
        <Stack fill vertical distribution="space-between">
            <Container padding="100">
                <Stack vertical spacing="300">
                    <span>
                        <LocalizedParagraph textKey={TextKey.EditItemExplanation} />
                    </span>
                    <LocalizedSubsectionTitle textKey={TextKey.ItemToBeEdited} />
                    <ExtendedOrderItemName orderItem={orderItem as unknown as OrderItem} />
                    <Grid templateColumns="0.5fr 0.5fr" gap="300">
                        <GridItem>
                            <TooltippedSpan
                                offset={-20}
                                tooltip={!orderItem.isQuantityEditable ? lt('CannotUpdateQuantityOfProduct') : null}
                            >
                                <FormCounterField
                                    min={1}
                                    size="medium"
                                    name={EditItemDialogFields.Quantity}
                                    disabled={!orderItem.isQuantityEditable}
                                    label={quantityLabel}
                                    helper={quantityBottomCaption}
                                />
                            </TooltippedSpan>
                        </GridItem>
                        <GridItem>
                            <TooltippedSpan
                                offset={-20}
                                tooltip={!orderItem.isPriceEditable ? lt('CannotUpdatePriceOfProduct') : null}
                            >
                                <CurrencyInputField
                                    label={lt(hasNetPricing ? 'PricePerUnitExclTax' : 'PricePerUnit')}
                                    name={EditItemDialogFields.UnitPrice}
                                    disabled={!orderItem.isPriceEditable}
                                    {...currencyLocalizationInfo}
                                    size="medium"
                                    helper={
                                        hasNetPricing ? (
                                            <Stack>
                                                {lt('PriceInclTax')}
                                                {UnicodeEntity.Colon}
                                                {UnicodeEntity.NoBreakSpace}
                                                <LocalizedCurrencyValue
                                                    value={{
                                                        value: unitPriceWithTax,
                                                        currency: orderItem.unitAmount.currency,
                                                    }}
                                                />
                                            </Stack>
                                        ) : null
                                    }
                                    captionIconWithTooltip={
                                        orderItem.isPriceEditable ? (
                                            <TooltippedIcon
                                                size="small"
                                                value={IconName.Info}
                                                tooltip={<CurrencyFormatHint />}
                                            />
                                        ) : null
                                    }
                                />
                            </TooltippedSpan>
                        </GridItem>
                    </Grid>
                    <FormTextareaField
                        name={EditItemDialogFields.Reason}
                        placeholder={lt('EnterReason')}
                        label={lt('ReasonForEdit')}
                        required
                        size="medium"
                        rows={1}
                    />
                </Stack>
            </Container>

            <Container padding="100">
                <EditItemDialogTotalAmount orderItem={orderItem as unknown as GeneratedOrderItem} />
            </Container>
        </Stack>
    );
});
